import { defineStore } from 'pinia';
import { VIDEO_CATALOG, PRODUCTS_US, PRODUCTS_CA, PRODUCT_LIMITS } from '@/front/constants/catalog';
import {
  US_PRODUCT_RULES,
  US_COMPLIANCE_CODES,
  US_VARIANT_LABELS,
  CA_CONTRACTING_FILTERS,
  LANGUAGE_OPTIONS
} from '@/front/constants/rules';

const cleanDigits = (value) => (value || '').replace(/\D/g, '').slice(0, 10);
const formatPhone = (value) => {
  const digits = cleanDigits(value);
  if (!digits) return '';
  const parts = [digits.slice(0, 3)];
  if (digits.length > 3) parts.push(digits.slice(3, 6));
  if (digits.length > 6) parts.push(digits.slice(6, 10));
  return parts.filter(Boolean).join('-');
};
const normalizePhone = (region, value) =>
  region === 'ca' || region === 'us' ? formatPhone(value) : value;

const isUsProductHidden = (productKey, selectedStates = []) => {
  if (!selectedStates.length) return false;
  const blocked = US_PRODUCT_RULES[productKey]?.blockedStates || [];
  if (!blocked.length) return false;
  return selectedStates.every((state) => blocked.includes(state));
};

const hiddenCaProducts = (contracting) => {
  const rule = CA_CONTRACTING_FILTERS[contracting] || CA_CONTRACTING_FILTERS.both;
  return new Set(rule.hide);
};

const buildUrlWithParams = (baseUrl, params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  return `${baseUrl}?${search.toString()}`;
};

export const useVideoFlowStore = defineStore('videoFlow', {
  state: () => ({
    region: 'us',
    uiLanguage: 'en', // For Canada toggle
    pageOne: {
      producerId: '',
      licenses: [],
      contracting: 'foresters'
    },
    pageTwo: {
      selectedProducts: [],
      name: '',
      phone: '',
      email: '',
      caLicenseNumber: '',
      selectedLanguages: ['en'],
      narrator: 'male',
      caslAcknowledged: false
    },
    excludedProducts: [],
    variants: [],
    submitted: false
  }),
  getters: {
    productsForRegion(state) {
      return state.region === 'us' ? PRODUCTS_US : PRODUCTS_CA;
    },
    productLimit(state) {
      return state.region === 'us' ? PRODUCT_LIMITS.us : PRODUCT_LIMITS.ca;
    },
    languageChoices(state) {
      return LANGUAGE_OPTIONS[state.region];
    },
    requiresCaLicense(state) {
      return state.region === 'us' && state.pageOne.licenses.includes('California');
    },
    availableProducts(state) {
      const base = this.productsForRegion;
      const excluded = new Set(state.excludedProducts || []);
      if (state.region !== 'us') {
        const hidden = hiddenCaProducts(state.pageOne.contracting);
        return base.filter((product) => !hidden.has(product.key) && !excluded.has(product.key));
      }
      return base.filter(
        (product) =>
          !isUsProductHidden(product.key, state.pageOne.licenses) &&
          !excluded.has(product.key)
      );
    }
  },
  actions: {
    setRegion(region) {
      this.region = region;
      this.uiLanguage = region === 'ca' ? this.uiLanguage : 'en';
      this.pageOne = {
        producerId: '',
        licenses: [],
        contracting: 'foresters'
      };
      this.pageTwo = {
        selectedProducts: [],
        name: '',
        phone: '',
        email: '',
        caLicenseNumber: '',
        selectedLanguages: region === 'ca' ? ['en'] : ['en'],
        narrator: 'male',
        caslAcknowledged: false
      };
      this.variants = [];
      this.submitted = false;
    },
    setUiLanguage(lang) {
      this.uiLanguage = lang;
    },
    setExcludedProducts(productKeys) {
      this.excludedProducts = productKeys || [];
    },
    updatePageOne(payload) {
      this.pageOne = { ...this.pageOne, ...payload };
      if (this.region === 'us' && !this.pageOne.licenses.includes('California')) {
        this.pageTwo.caLicenseNumber = '';
      }
    },
    updatePageTwo(payload) {
      const next = { ...this.pageTwo, ...payload };
      if (Object.prototype.hasOwnProperty.call(payload, 'phone')) {
        next.phone = normalizePhone(this.region, payload.phone);
      }
      next.selectedProducts = (next.selectedProducts || []).slice(0, this.productLimit);
      next.selectedLanguages = payload.selectedLanguages || this.pageTwo.selectedLanguages;
      next.narrator = payload.narrator || this.pageTwo.narrator || 'male';
      this.pageTwo = next;
    },
    toggleProduct(productKey) {
      if (this.region === 'us') {
        this.pageTwo.selectedProducts = [productKey];
        return;
      }
      const current = new Set(this.pageTwo.selectedProducts);
      if (current.has(productKey)) {
        current.delete(productKey);
      } else if (current.size < this.productLimit) {
        current.add(productKey);
      }
      this.pageTwo.selectedProducts = Array.from(current);
    },
    setLanguages(languages) {
      this.pageTwo.selectedLanguages = languages;
    },
    buildVariants({ persist } = { persist: false }) {
      const products = this.pageTwo.selectedProducts;
      const languages = this.pageTwo.selectedLanguages;
      const params = {
        name: this.pageTwo.name,
        phone: this.pageTwo.phone,
        email: this.pageTwo.email,
        producerId: this.pageOne.producerId,
        caLicenseNumber: this.requiresCaLicense ? this.pageTwo.caLicenseNumber : '',
        region: this.region,
        narrator: this.pageTwo.narrator
      };

      const hasCalifornia = this.pageOne.licenses.includes('California');
      const variants = [];
      products.forEach((productKey) => {
        const rule = US_PRODUCT_RULES[productKey];
        const versions = [];
        if (this.region !== 'us' || !rule?.caVariants || !hasCalifornia) {
          versions.push('standard');
        } else {
          const blockedStates = rule?.blockedStates || [];
          const hasNonCaAllowed = this.pageOne.licenses.some(
            (state) => state !== 'California' && !blockedStates.includes(state)
          );
          versions.push('ca');
          if (hasNonCaAllowed) {
            versions.push('standard');
          }
        }

        languages.forEach((language) => {
          versions.forEach((version) => {
            const match = VIDEO_CATALOG.find(
              (video) =>
                video.region === this.region &&
                video.productKey === productKey &&
                video.language === language &&
                video.narrator === this.pageTwo.narrator &&
                (video.version || 'standard') === version
            );
            if (!match) return;

            const url = buildUrlWithParams(match.baseUrl, {
              name: params.name,
              phone: params.phone,
              email: params.email,
              producer_id: params.producerId,
              product: productKey,
              language,
              region: this.region,
              narrator: params.narrator,
              ca_license_number: params.caLicenseNumber || undefined,
              license: params.caLicenseNumber || undefined
            });

            const complianceCode =
              this.region === 'us'
                ? US_COMPLIANCE_CODES?.[productKey]?.[version]?.[language]?.[
                    params.narrator
                  ] ||
                  US_COMPLIANCE_CODES?.[productKey]?.standard?.[language]?.[
                    params.narrator
                  ]
                : undefined;

            variants.push({
              ...match,
              id: version === 'standard' ? match.id : `${match.id}-${version}`,
              url,
              version,
              versionLabel:
                this.region === 'us' ? US_VARIANT_LABELS[version] || version : undefined,
              complianceCode
            });
          });
        });
      });

      if (persist) {
        this.variants = variants;
        this.submitted = true;
      }

      return variants;
    },
    generateVariants() {
      return this.buildVariants({ persist: true });
    },
    resetFlow() {
      this.pageOne = {
        producerId: '',
        licenses: [],
        contracting: 'foresters'
      };
      this.pageTwo = {
        selectedProducts: [],
        name: '',
        phone: '',
        email: '',
        caLicenseNumber: '',
        selectedLanguages: this.region === 'ca' ? ['en'] : ['en'],
        narrator: 'male',
        caslAcknowledged: false
      };
      this.variants = [];
      this.submitted = false;
    }
  }
});
